/**
 * PATH: packages/ui/src/components/global-search/queryCache.ts
 * FILE_ID: UI-GLOBALSEARCH-QUERYCACHE-001
 * PURPOSE: Short-TTL LRU cache of per-provider search results so repeating
 *          a recent query (backspacing, re-opening the palette, toggling a
 *          chip) paints instantly instead of re-fanning to the network.
 * WHY: Search-architecture plan W5 (2026-07-07, "nothing in the plan
 *      optimises speed"). Typing "hudso" → "hudson" → backspace → "hudso"
 *      re-fired every provider for a query answered 2 seconds ago. A tiny
 *      session cache (in-memory, 30s TTL, 40 entries) removes the repeat
 *      round-trips without any staleness risk worth engineering around —
 *      board/email content does not meaningfully change inside 30s, and a
 *      palette close/open within the TTL hits the same fresh rows.
 * FLOW: (providerId, query) → hit? rows (instant paint) : miss → provider
 *       fan-out → store rows on success
 * DEPENDENCIES: none. Module-level singleton — the palette is a singleton
 *      surface per document, and tests can reset via clearQueryCache().
 * RELATED: useSearch.ts (sole consumer, around the provider task loop)
 */

import type { SearchResult } from './types'

interface CacheEntry {
  rows: SearchResult[]
  at: number
}

const TTL_MS = 30_000
const MAX_ENTRIES = 40

/** Insertion-ordered Map doubles as the LRU: hits re-insert, evictions
 *  pop the oldest key. */
const cache = new Map<string, CacheEntry>()

function keyOf(providerId: string, query: string): string {
  return `${providerId}\u0000${query.trim().toLowerCase()}`
}

/** Cached rows for (provider, query), or undefined on miss/expiry.
 *  A hit refreshes LRU recency but NOT the TTL clock. */
export function getCachedResults(
  providerId: string,
  query: string,
  now: number = Date.now(),
): SearchResult[] | undefined {
  const key = keyOf(providerId, query)
  const entry = cache.get(key)
  if (!entry) return undefined
  if (now - entry.at > TTL_MS) {
    cache.delete(key)
    return undefined
  }
  // LRU touch — re-insert at the tail.
  cache.delete(key)
  cache.set(key, entry)
  return entry.rows
}

/** Store a provider's rows for a query, evicting the least-recently-used
 *  entry past the cap. Rows are stored as-is (already-tagged results). */
export function setCachedResults(
  providerId: string,
  query: string,
  rows: SearchResult[],
  now: number = Date.now(),
): void {
  const key = keyOf(providerId, query)
  cache.delete(key)
  cache.set(key, { rows, at: now })
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value
    if (oldest === undefined) break
    cache.delete(oldest)
  }
}

/** Test/HMR hook — wipe the session cache. */
export function clearQueryCache(): void {
  cache.clear()
}
